'use client';
import { StaticImageData } from 'next/image';
import { useState } from 'react';

import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';

import EnvironmentIcon from '@i/careers/environment-icon.png';
import FlexibilityIcon from '@i/careers/flexibility-icon.png';
import GrowthIcon from '@i/careers/growth-icon.png';
import MobileHeaderImage from '@i/careers/header-mobile.png';
import HeaderImage from '@i/careers/header.png';
import TeamworkIcon from '@i/careers/teamwork-icon.png';

import JobModal from './JobModal';
import { JobOpening, jobOpenings } from './jobs';

type Perk = {
  icon: StaticImageData;
  title: string;
  points: string[];
};

const perks: Perk[] = [
  {
    icon: FlexibilityIcon,
    title: 'Flexibility',
    points: [
      'Work from home with room to manage your schedule.',
      'Make time for family, hobbies, travel, or the reset you need.',
      'Stay productive without being tied to a traditional office routine.',
    ],
  },
  {
    icon: TeamworkIcon,
    title: 'Teamwork',
    points: [
      'Work with a passionate team building for riders and powersports enthusiasts.',
      'Collaborate across departments to solve real marketplace challenges.',
      'Bring ideas, feedback, and ownership to a team that moves together.',
    ],
  },
  {
    icon: GrowthIcon,
    title: 'Growth',
    points: [
      'Join a fast-moving company with room to grow as the business grows.',
      'Take on new responsibilities and build skills across projects.',
      'Help shape a marketplace that is still evolving.',
    ],
  },
  {
    icon: EnvironmentIcon,
    title: 'Environment',
    points: [
      'Work in an industry built around gear, riding, and powersports culture.',
      'Be part of a team that keeps the work fun, focused, and energetic.',
      'Build something meaningful for a community we actually care about.',
    ],
  },
];

const headingCss =
  'text-[3rem] font-bold leading-[1.3] tracking-[-0.01em] text-brand-black md:text-[3.75rem]';
const bodyCss = 'text-[1.75rem] font-medium leading-[1.5] text-text-muted';

const Careers = () => {
  const [selectedJob, setSelectedJob] = useState<JobOpening | null>(null);

  return (
    <>
      <section className="relative w-full overflow-hidden bg-brand-black">
        <SafeImage
          src={HeaderImage}
          alt="MX Locker riders"
          width={1440}
          height={400}
          priority
          classes="hidden h-[50rem] w-full object-cover md:block"
        />
        <SafeImage
          src={MobileHeaderImage}
          alt="MX Locker riders"
          width={768}
          height={200}
          priority
          classes="h-[25rem] w-full object-cover md:hidden"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <h1 className="px-[2rem] text-center text-[3rem] font-bold leading-[1.2] tracking-[-0.01em] text-white md:text-[4.75rem]">
            Work at MX Locker
          </h1>
        </div>
      </section>

      <section className="mx-auto flex w-full max-w-default flex-col items-start gap-[3rem] px-[2rem] py-[3rem] md:items-center md:gap-[5rem] md:py-[8rem]">
        <h2 className={headingCss}>Open positions</h2>
        <div className="flex w-full max-w-[99rem] flex-col gap-[2rem]">
          {jobOpenings.map((job) => (
            <div
              key={job.id}
              className="flex flex-col gap-[2rem] rounded-[2rem] bg-brand-off-white p-[2rem] sm:flex-row sm:items-center sm:justify-between sm:px-[4rem] sm:py-[3rem]"
            >
              <div className="flex flex-col gap-[0.5rem]">
                <p className="text-[2.25rem] font-bold tracking-[-0.01em] text-brand-black">
                  {job.title}
                </p>
                <p className={bodyCss}>{job.location}</p>
              </div>
              <Button
                type="secondary"
                text="Apply"
                height="small"
                onClick={() => setSelectedJob(job)}
                className="self-end px-[3rem] text-[1.75rem] sm:self-auto sm:px-[5rem]"
              />
            </div>
          ))}
        </div>
        <p className={bodyCss}>More coming soon!</p>
      </section>

      <section className="mx-auto flex w-full max-w-default flex-col items-start gap-[3rem] px-[2rem] py-[5rem] md:items-center md:gap-[5rem] md:py-[10rem]">
        <div className="flex w-full max-w-[124.5rem] flex-col items-start gap-[2rem] md:items-center">
          <h2 className={`${headingCss} text-left md:text-center`}>
            Why work at MX Locker?
          </h2>
          <p className={`${bodyCss} w-full text-left md:text-center`}>
            We connect riders across the world to shop and sell almost anything -
            riding gear, bike parts and dirt bikes. We all have things we
            don&apos;t use, never used, or simply outgrew. Our mission is to make
            dirt bike riding more affordable and accessible to our community.
          </p>
        </div>

        <div className="grid w-full max-w-[124.5rem] grid-cols-1 gap-x-[3rem] gap-y-[4rem] md:grid-cols-2 md:gap-y-[5rem]">
          {perks.map((perk) => (
            <div
              key={perk.title}
              className="flex flex-col gap-[2rem] md:gap-[3rem] md:pr-[4rem]"
            >
              <SafeImage
                src={perk.icon}
                alt={perk.title}
                width={100}
                height={100}
                classes="h-[12.5rem] w-[12.5rem] object-contain"
              />
              <div className="flex flex-col gap-[2rem]">
                <h3 className="text-[3rem] font-bold tracking-[-0.01em] text-brand-black">
                  {perk.title}
                </h3>
                <ul className="flex flex-col gap-[1rem]">
                  {perk.points.map((point) => (
                    <li
                      key={point}
                      className={`flex items-start gap-[1.2rem] ${bodyCss}`}
                    >
                      <span className="mt-[0.7rem] h-[1rem] w-[1rem] shrink-0 rounded-full bg-brand-secondary" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="h-px w-full max-w-[124.5rem] bg-brand-lighter-gray" />

        <p className={`${bodyCss} w-full max-w-[124.5rem]`}>
          MX Locker was started by our founder Ryan Amoils after realizing there
          was no central place for the dirt bike community to buy and sell riding
          gear, bike parts and dirt bikes. Since the founding of MX Locker,
          we&apos;ve grown from a single seller platform to a community of
          1,000,000+ members from around the world.
        </p>
      </section>

      <JobModal
        job={selectedJob}
        isOpen={!!selectedJob}
        dismiss={() => setSelectedJob(null)}
      />
    </>
  );
};

export default Careers;
